import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/NavigationBar';
import NotificationBanner from '../../components/NotificationBanner';
import MetricsCard from './components/MetricsCard';
import PipelineDonutChart from './components/PipelineDonutChart';
import RevenueLineChart from './components/RevenueLineChart';
import SalesRepBarChart from './components/SalesRepBarChart';
import ForecastAreaChart from './components/ForecastAreaChart';
import FilterControls from './components/FilterControls';
import Icon from '../../components/AppIcon';
import { dealsService } from '../../services/crmService';

const DealAnalyticsDashboard = () => {
  const [showNotification, setShowNotification] = useState(true);
  const [dateRange, setDateRange] = useState('quarterly');
  const [selectedStage, setSelectedStage] = useState('all');
  const [selectedRep, setSelectedRep] = useState('all');
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDeals();
    const unsubscribe = dealsService?.subscribeToChanges(() => loadDeals());
    return () => unsubscribe();
  }, []);

  const loadDeals = async () => {
    try {
      setLoading(true);
      const data = await dealsService?.getAll();
      setDeals(data);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load deals');
      console.error('Error loading deals:', err);
    } finally {
      setLoading(false);
    }
  };

  // Format currency in Indian Rupees
  const formatINR = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    })?.format(value);
  };

  // Calculate win rate
  const calculateWinRate = () => {
    const closedDeals = deals?.filter(d => d?.stage === 'Closed Won' || d?.stage === 'Closed Lost');
    const wonDeals = deals?.filter(d => d?.stage === 'Closed Won');
    if (closedDeals?.length === 0) return 0;
    return ((wonDeals?.length / closedDeals?.length) * 100)?.toFixed(1);
  };

  // Calculate average deal size
  const calculateAvgDealSize = () => {
    const closedWonDeals = deals?.filter(d => d?.stage === 'Closed Won');
    if (closedWonDeals?.length === 0) return 0;
    const totalValue = closedWonDeals?.reduce((sum, d) => sum + (d?.dealValue || 0), 0);
    return Math.round(totalValue / closedWonDeals?.length);
  };

  // Calculate pipeline velocity (avg days to close)
  const calculatePipelineVelocity = () => {
    const closedWonDeals = deals?.filter(d => d?.stage === 'Closed Won' && d?.createdAt && d?.updatedAt);
    if (closedWonDeals?.length === 0) return 0;
    const totalDays = closedWonDeals?.reduce((sum, d) => {
      const created = new Date(d?.createdAt);
      const updated = new Date(d?.updatedAt);
      const days = Math.floor((updated - created) / (1000 * 60 * 60 * 24));
      return sum + days;
    }, 0);
    return Math.round(totalDays / closedWonDeals?.length);
  };

  // Calculate Q1 forecast
  const calculateQ1Forecast = () => {
    const activeDeals = deals?.filter(d => !['Closed Won', 'Closed Lost']?.includes(d?.stage));
    const totalPipelineValue = activeDeals?.reduce((sum, d) => sum + (d?.dealValue || 0), 0);
    const winRate = calculateWinRate() / 100;
    return Math.round(totalPipelineValue * winRate);
  };

  // Key metrics data
  const metricsData = [
    {
      title: 'Win Rate',
      value: `${calculateWinRate()}%`,
      icon: 'TrendingUp',
      trend: 'up',
      trendValue: '+5.2%',
      color: 'success',
      description: 'vs last quarter'
    },
    {
      title: 'Avg Deal Size',
      value: formatINR(calculateAvgDealSize()),
      icon: 'IndianRupee',
      trend: 'up',
      trendValue: '+12.8%',
      color: 'primary',
      description: 'per closed deal'
    },
    {
      title: 'Pipeline Velocity',
      value: `${calculatePipelineVelocity()} days`,
      icon: 'Zap',
      trend: 'down',
      trendValue: '-4 days',
      color: 'secondary',
      description: 'avg time to close'
    },
    {
      title: 'Q1 Forecast',
      value: formatINR(calculateQ1Forecast()),
      icon: 'Target',
      trend: 'up',
      trendValue: '+18%',
      color: 'warning',
      description: 'projected revenue'
    }
  ];

  // Pipeline stage distribution data
  const calculatePipelineData = () => {
    const stages = ['Lead', 'Qualified', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost'];
    const colors = ['#A8C8EC', '#8B7EC8', '#F4A6CD', '#9AE6B4', '#68D391', '#FEB2B2'];
    
    return stages?.map((stage, index) => {
      const stageDeals = deals?.filter(d => d?.stage === stage);
      const totalDeals = deals?.length || 1;
      return {
        name: stage,
        value: Math.round((stageDeals?.length / totalDeals) * 100),
        deals: stageDeals?.length,
        color: colors?.[index]
      };
    });
  };

  // Monthly revenue trend data
  const calculateRevenueData = () => {
    const months = ['Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];
    const currentMonth = new Date()?.getMonth();
    const currentYear = new Date()?.getFullYear();
    
    return months?.map((month, index) => {
      const monthIndex = (currentMonth - 6 + index + 12) % 12;
      const year = monthIndex > currentMonth ? currentYear - 1 : currentYear;
      
      const monthDeals = deals?.filter(d => {
        if (!d?.updatedAt) return false;
        const dealDate = new Date(d?.updatedAt);
        return dealDate?.getMonth() === monthIndex && dealDate?.getFullYear() === year && d?.stage === 'Closed Won';
      });
      
      const revenue = monthDeals?.reduce((sum, d) => sum + (d?.dealValue || 0), 0);
      const target = revenue * 0.9; // Target is 90% of actual for demo
      
      return { month, revenue, target };
    });
  };

  // Sales rep performance data
  const calculateSalesRepData = () => {
    const repMap = {};
    
    deals?.forEach(deal => {
      const rep = deal?.salesRep || 'Unassigned';
      if (!repMap?.[rep]) {
        repMap[rep] = { name: rep, deals: 0, revenue: 0 };
      }
      if (deal?.stage === 'Closed Won') {
        repMap[rep].deals += 1;
        repMap[rep].revenue += deal?.dealValue || 0;
      }
    });
    
    return Object.values(repMap)
      ?.sort((a, b) => b?.revenue - a?.revenue)
      ?.slice(0, 6);
  };

  // Quarterly forecast data
  const calculateForecastData = () => {
    const quarters = ['Q3 2025', 'Q4 2025', 'Q1 2026', 'Q2 2026'];
    const avgQuarterlyRevenue = deals
      ?.filter(d => d?.stage === 'Closed Won')
      ?.reduce((sum, d) => sum + (d?.dealValue || 0), 0) / 2; // Divide by 2 for quarterly avg
    
    return quarters?.map((quarter, index) => {
      const isHistorical = index < 2;
      const forecast = avgQuarterlyRevenue * (1 + index * 0.1); // 10% growth per quarter
      
      return {
        quarter,
        actual: isHistorical ? forecast * 0.95 : null,
        forecast: forecast,
        lower: forecast * 0.9,
        upper: forecast * 1.1
      };
    });
  };

  const handleExport = (format) => {
    console.log(`Exporting analytics report as ${format}`);
    // Export functionality would be implemented here
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="pt-20 flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      {error && (
        <NotificationBanner
          message={error}
          type="error"
          onDismiss={() => setError('')}
        />
      )}
      {showNotification && !error && (
        <NotificationBanner
          message="Analytics data updated in real-time from your CRM database"
          onDismiss={() => setShowNotification(false)}
        />
      )}
      
      <div className="pt-20 px-4 md:px-6 lg:px-8 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
                  Deal Analytics Dashboard
                </h1>
                <p className="text-muted-foreground">
                  Comprehensive sales performance analysis and revenue forecasting
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleExport('pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  <Icon name="FileDown" size={18} />
                  <span className="text-sm font-medium">Export PDF</span>
                </button>
                <button
                  onClick={() => handleExport('excel')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
                >
                  <Icon name="FileSpreadsheet" size={18} />
                  <span className="text-sm font-medium">Export Excel</span>
                </button>
              </div>
            </div>
          </div>

          {/* Filter Controls */}
          <FilterControls
            dateRange={dateRange}
            setDateRange={setDateRange}
            selectedStage={selectedStage}
            setSelectedStage={setSelectedStage}
            selectedRep={selectedRep}
            setSelectedRep={setSelectedRep}
          />

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {metricsData?.map((metric, index) => (
              <MetricsCard key={index} {...metric} />
            ))}
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Pipeline Distribution */}
            <PipelineDonutChart data={calculatePipelineData()} />

            {/* Revenue Forecast */}
            <ForecastAreaChart data={calculateForecastData()} formatINR={formatINR} />
          </div>

          {/* Revenue Trend Chart */}
          <div className="mb-6">
            <RevenueLineChart data={calculateRevenueData()} formatINR={formatINR} />
          </div>

          {/* Sales Rep Performance */}
          <div>
            <SalesRepBarChart data={calculateSalesRepData()} formatINR={formatINR} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DealAnalyticsDashboard;